"use client"

import { VoteStatus, RaffleStatus } from "@repo/loomlock/client"
import {
  IVote,
  IRaffle,
  IGiveaway,
  IGachabox,
  GiveawayStatus,
} from "@repo/loomlock/model-types"
import { MonthGroup } from "./MonthGroup"
import { format } from "date-fns"
import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import { useMemo, useState } from "react"
import { Segmented, Swap } from "@abraxas/ui/ui"
import { ArchiveIcon, Calendar } from "lucide-react"
import { Loading } from "@/shared/components/Loading"
import { PromotedGachaboxCard } from "@/shared/components/PromotedGachaboxCard"

type Event =
  | (IVote & { status?: VoteStatus })
  | (IRaffle & { status?: RaffleStatus })
  | (IGiveaway & { status?: GiveawayStatus })
  | IGachabox

export function EventsPageClient() {
  const [showUpcoming, setShowUpcoming] = useState(true)

  const { data: events = [], isLoading } = useQuery<Event[]>({
    queryKey: ["events"],
    queryFn: async () => {
      const response = await axios.get("/api/events")
      return response.data
    },
  })

  const filteredEvents = useMemo(() => {
    const now = new Date()
    return events.filter((event) => {
      if ((event as IGachabox).promoted) {
        return false
      }
      const hasEnd = Boolean(event.endTime)
      const isOngoing = !hasEnd || new Date(event.endTime) >= now
      if (showUpcoming) {
        return isOngoing
      }
      return hasEnd && !isOngoing
    })
  }, [events, showUpcoming])

  const groupedEvents = useMemo(() => {
    const now = new Date()
    const currentEvents: Event[] = []
    const groups: Record<string, Event[]> = {}

    filteredEvents.forEach((event) => {
      const isCurrent =
        showUpcoming && new Date(event.startTime).getTime() <= now.getTime()
      if (isCurrent) {
        currentEvents.push(event)
        return
      }
      const monthYear = format(new Date(event.startTime), "MMMM yyyy")
      if (!groups[monthYear]) {
        groups[monthYear] = []
      }
      groups[monthYear].push(event)
    })

    const monthGroups = Object.entries(groups).sort(([monthA], [monthB]) => {
      const firstEventA = groups[monthA]?.[0]
      const firstEventB = groups[monthB]?.[0]
      if (!firstEventA || !firstEventB) {
        return 0
      }
      const dateA = new Date(firstEventA.startTime)
      const dateB = new Date(firstEventB.startTime)

      if (showUpcoming) {
        return dateA.getTime() - dateB.getTime()
      }
      return dateB.getTime() - dateA.getTime()
    })

    currentEvents.sort(
      (a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    )

    return currentEvents.length > 0
      ? ([["Now", currentEvents], ...monthGroups] as [string, Event[]][])
      : monthGroups
  }, [filteredEvents, showUpcoming])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loading />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col items-start gap-3 max-w-[]">
        <PromotedGachaboxCard />
      </div>
      <div className="flex items-center gap-3">
        <Segmented
          options={[
            {
              label: (
                <div className="flex items-center gap-2 w-48 py-1.5 justify-center">
                  <Calendar className="h-4 w-4" />
                  <span className="text-body-s">Upcoming</span>
                </div>
              ),
              value: "upcoming",
            },
            {
              label: (
                <div className="flex items-center gap-2 w-48 py-1.5 justify-center">
                  <ArchiveIcon className="h-4 w-4" />
                  <span className="text-body-s">Past</span>
                </div>
              ),
              value: "past",
            },
          ]}
          value={showUpcoming ? "upcoming" : "past"}
          onChange={(value) => setShowUpcoming(value === "upcoming")}
        />
      </div>

      <Swap
        motionKey={showUpcoming ? "upcoming" : "past"}
        className="flex flex-col gap-6 max-w-[848px]"
        transitionType="slide"
      >
        {filteredEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <p className="font-medium text-body-l text-white">
              No {showUpcoming ? "upcoming" : "past"} events available.
            </p>
            <p className="font-medium text-m_body-m text-neutral-grey-400 mt-2">
              {showUpcoming
                ? "Check back soon for upcoming events!"
                : "Switch to upcoming events to see what's next!"}
            </p>
          </div>
        ) : (
          <>
            {groupedEvents.map(([month, monthEvents]) => (
              <MonthGroup key={month} month={month} events={monthEvents} />
            ))}
          </>
        )}
      </Swap>
    </div>
  )
}
